import { holdsAnywhere } from '@carii/casl-abilities';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Badge, StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useListControls } from '../../hooks/useListControls.js';
import {
  type CodeApprovalFilter,
  type CodeCancellationFilter,
  type DiscountCode,
  discountsApi,
} from '../../services/discounts.js';
import { CancellationCell } from './CodeCancelCells.js';
import {
  GivenByCell,
  ShareApprovalCell,
  ShareBookingCell,
  SharedWithCell,
} from './CodeShareCells.js';
import { SharedActionBar } from './SharedActionBar.js';

const shareColumns = (discountId: string): Column<DiscountCode>[] => [
  { key: 'code', header: 'Code' },
  {
    key: 'status',
    header: 'Status',
    // 411: an approved cancellation sets `revoked`, read as Cancelled.
    render: (c) =>
      c.status === 'revoked' ? (
        <Badge variant="danger">Cancelled</Badge>
      ) : (
        <StatusBadge status={c.status} />
      ),
  },
  // 391 (#1312): who it was shared with, for which booking, by whom, why, and its approval.
  { key: 'shared_with', header: 'Shared with', render: (c) => <SharedWithCell code={c} /> },
  { key: 'share_booking_id', header: 'For booking', render: (c) => <ShareBookingCell code={c} /> },
  { key: 'shared_by', header: 'Given by', render: (c) => <GivenByCell code={c} /> },
  {
    key: 'share_reason',
    header: 'Reason',
    render: (c) => <span className="text-sm">{c.share_reason ?? '—'}</span>,
  },
  {
    key: 'share_approval',
    header: 'Approval',
    render: (c) => <ShareApprovalCell code={c} discountId={discountId} />,
  },
  // 411 (#1513): where the code's cancellation stands.
  {
    key: 'cancellation',
    header: 'Cancellation',
    render: (c) => <CancellationCell code={c} discountId={discountId} />,
  },
  { key: 'used_by', header: 'Used By', render: (c) => c.used_by?.label ?? '—' },
  {
    key: 'used_at',
    header: 'Used At',
    render: (c) => (c.used_at ? new Date(c.used_at).toLocaleString('en-MY') : '—'),
  },
  { key: 'booking_id', header: 'Booking', render: (c) => c.booking_id ?? '—' },
];

export function DiscountCodeListPage() {
  const { id } = useParams<{ id: string }>();
  const ability = useAbility();
  // 372: the server now asks for `update` (Admin holds it through manage).
  const canManage = holdsAnywhere(ability as never, 'update', 'Discount');
  /**
   * 164: this screen was missed by 162 — its scope table said "discounts (×2)" and only the
   * campaign list got a search box. A campaign can hold hundreds of generated codes, which is
   * exactly where finding one by typing part of it matters most.
   */
  const { page, setPage, searchInput, setSearchInput, search } = useListControls();
  // 391: approval filter (replaces 212's shared/unshared select).
  const [approval, setApproval] = useState<CodeApprovalFilter | ''>('');
  // 411: cancellation filter.
  const [cancellation, setCancellation] = useState<CodeCancellationFilter | ''>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['discount-codes', id, page, search, approval, cancellation],
    queryFn: () =>
      discountsApi.listCodes(
        id as string,
        page,
        50,
        search || undefined,
        approval || undefined,
        cancellation || undefined,
      ),
    enabled: Boolean(id),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  const pages = data?.pages ?? 1;
  const items = data?.items ?? [];

  // 037/212: per-page multi-select, mirroring `UserListPage.tsx` — selection never
  // survives a page/filter change.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // biome-ignore lint/correctness/useExhaustiveDependencies: page/filter changes intentionally reset the selection
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, search, approval, cancellation]);

  const columns = useMemo(() => shareColumns(id as string), [id]);
  const codeLabels = useMemo(() => Object.fromEntries(items.map((c) => [c.id, c.code])), [items]);
  const pendingCount = data?.pending_count ?? 0;
  const chips: Array<[CodeApprovalFilter | '', string]> = [
    ['', 'All'],
    ['pending', `Pending approval${pendingCount ? ` (${pendingCount})` : ''}`],
    ['approved', 'Approved'],
    ['rejected', 'Rejected'],
    ['unshared', 'Not shared'],
  ];
  const cancellationPending = data?.cancellation_pending_count ?? 0;
  const cancellationChips: Array<[CodeCancellationFilter | '', string]> = [
    ['', 'All'],
    ['pending', `Cancellation pending${cancellationPending ? ` (${cancellationPending})` : ''}`],
    ['cancelled', 'Cancelled'],
  ];
  // 411: the selected codes whose cancellation this caller may decide (the platform's answer).
  const cancelDecisionIds = items
    .filter((c) => selectedIds.has(c.id) && c.cancellation_decision_for === 'allowed')
    .map((c) => c.id);

  function toggleRow(rowId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }

  function toggleAllPage() {
    setSelectedIds((prev) => {
      const allSelected = items.length > 0 && items.every((c) => prev.has(c.id));
      return allSelected ? new Set<string>() : new Set(items.map((c) => c.id));
    });
  }

  return (
    <div>
      <PageHeader title="Discount Codes" />
      <div className="mb-3 flex flex-wrap gap-3">
        <ListSearch
          value={searchInput}
          onChange={setSearchInput}
          label="Search codes"
          testId="discount-code-search"
        />
      </div>
      <fieldset aria-label="Approval" className="mb-3 flex flex-wrap gap-2">
        {chips.map(([v, label]) => (
          <Button
            key={v || 'all'}
            size="sm"
            variant={approval === v ? 'primary' : 'secondary'}
            aria-pressed={approval === v}
            onClick={() => {
              setApproval(v);
              setPage(1);
            }}
          >
            {label}
          </Button>
        ))}
      </fieldset>
      <fieldset aria-label="Cancellation" className="mb-3 flex flex-wrap gap-2">
        {cancellationChips.map(([v, label]) => (
          <Button
            key={v || 'all'}
            size="sm"
            variant={cancellation === v ? 'primary' : 'secondary'}
            aria-pressed={cancellation === v}
            onClick={() => {
              setCancellation(v);
              setPage(1);
            }}
          >
            {label}
          </Button>
        ))}
      </fieldset>
      {id && (
        <SharedActionBar
          discountId={id}
          selectedIds={selectedIds}
          codeLabels={codeLabels}
          cancelDecisionIds={cancelDecisionIds}
          onClear={() => setSelectedIds(new Set())}
        />
      )}
      <DataTable
        columns={columns}
        data={items}
        loading={isLoading}
        keyExtractor={(r) => r.id}
        selectable={canManage}
        selectedIds={selectedIds}
        onToggleRow={toggleRow}
        onToggleAllPage={toggleAllPage}
        // 063: administrator-only. An empty campaign and a refused one are
        // different facts.
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: pages,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
